import type { HoComponents } from '@pos/api-client';
import { hoApi } from '../../api';

type Dto = HoComponents['schemas'];
export type TranslationText = Dto['TranslationTextDto'];
export type TranslationPage = Dto['TranslationPageDto'];
export type BrandTranslationSummary = Dto['BrandTranslationSummaryDto'];
export type TranslationChange = Dto['TranslationChange'];
export type SaveTranslationsResult = Dto['SaveTranslationsResultDto'];

/** The server checks these again: whoever keeps the catalog translates it (FR-ONL-19). */
export const TRANSLATIONS_READ = 'ho.catalog.translations.read';
export const TRANSLATIONS_MANAGE = 'ho.catalog.translations.manage';

/** The kinds of text, in the order a customer meets them on the menu. */
export const TEXT_KINDS = ['category', 'item', 'modifier_group', 'item_group', 'dimension', 'dimension_value', 'deal', 'deal_component'] as const;
export type TextKind = (typeof TEXT_KINDS)[number];

/** The most changes the server takes in one save; an import sends larger files in parts. */
export const MAX_CHANGES = 2000;

export const translationKeys = {
  all: ['ho', 'catalog', 'translations'] as const,
  languages: ['ho', 'catalog', 'translations', 'languages'] as const,
  summary: ['ho', 'catalog', 'translations', 'summary'] as const,
  page: (brandId: string, kind: string, missingIn: string, search: string, page: number, pageSize: number) =>
    ['ho', 'catalog', 'translations', 'page', brandId, kind, missingIn, search, page, pageSize] as const,
};

export async function getTextLanguages(): Promise<string[]> {
  const { data, error } = await hoApi.GET('/api/v1/catalog/translations/languages');
  if (!data) throw error;
  return data;
}

export async function getSummary(): Promise<BrandTranslationSummary[]> {
  const { data, error } = await hoApi.GET('/api/v1/catalog/translations/summary');
  if (!data) throw error;
  return data;
}

export async function listTexts(query: { brandId: string; kind?: string; missingIn?: string; search?: string; page: number; pageSize: number }): Promise<TranslationPage> {
  const { data, error } = await hoApi.GET('/api/v1/catalog/translations', {
    params: { query: { ...query, kind: query.kind || undefined, missingIn: query.missingIn || undefined, search: query.search || undefined } },
  });
  if (!data) throw error;
  return data;
}

export async function saveTexts(brandId: string, changes: TranslationChange[]): Promise<SaveTranslationsResult> {
  const { data, error } = await hoApi.PUT('/api/v1/catalog/translations', { body: { brandId, changes } });
  if (!data) throw error;
  return data;
}

/** The brand's texts as CSV, made into a file in the browser (no link with a token). */
export async function downloadTexts(brandId: string): Promise<Blob> {
  const { data, response } = (await hoApi.GET('/api/v1/catalog/translations/export', { params: { query: { brandId } }, parseAs: 'blob' } as never)) as {
    data?: unknown;
    response: Response;
  };
  if (!response.ok || !(data instanceof Blob)) throw new Error(String(response.status));
  return data;
}
